import { useCallback, useEffect } from "react";
import { useDispatch } from "react-redux";
import { bh_main_steps, bh_sub_steps } from "helpers/enum";
import { behaviorHealthVisitFlowReducers } from "reducers/behavior_health/behaviorHealthVisitFlowReducer";

function usePopulateBehaviorHealthData({ incompleteData, patientsList }) {
  const dispatch = useDispatch();

  const updateMainSteps = useCallback(() => {
    const apiMainStepKey = incompleteData?.behavior_health_steps?.main_steps;
    const mainStepsArray = [
      bh_main_steps.visit_details,
      bh_main_steps.main_select_slot,
      bh_main_steps.basic_info,
      bh_main_steps.additional_details,
    ];
    mainStepsArray.forEach((el) => {
      if (
        apiMainStepKey &&
        apiMainStepKey?.hasOwnProperty(bh_main_steps?.[el])
      ) {
        dispatch(
          behaviorHealthVisitFlowReducers?.[bh_main_steps?.[el]]({
            status: apiMainStepKey?.[bh_main_steps?.[el]],
          })
        );
      }
    });
    dispatch(
      behaviorHealthVisitFlowReducers.completedProgress({
        value: incompleteData?.meta?.progress_percentage,
      })
    );
  }, [
    dispatch,
    incompleteData?.behavior_health_steps?.main_steps,
    incompleteData?.meta?.progress_percentage,
  ]);

  const updateVisitDetails = useCallback(() => {
    const patientId = incompleteData?.patient_id;
    let selectPatientStatus;

    const patientData = patientsList?.find(
      (patient) => patient?.id === patientId
    );

    if (patientData?.relationship_with_account === "myself") {
      selectPatientStatus = "Me";
    } else if (patientData?.relationship_with_account === "pappa") {
      selectPatientStatus = "My Child";
    } else {
      selectPatientStatus = "Someone else";
    }

    dispatch(
      behaviorHealthVisitFlowReducers.select_patient({
        value: selectPatientStatus,
        isValid: selectPatientStatus ? true : false,
        status:
          incompleteData?.behavior_health_steps?.sub_steps?.[
            bh_sub_steps.select_patient
          ] ?? "upcoming",
      })
    );
    if (selectPatientStatus === "My Child") {
      dispatch(
        behaviorHealthVisitFlowReducers.select_child({
          value: patientData?.full_name,
        })
      );
    } else if (selectPatientStatus === "Someone else") {
      dispatch(
        behaviorHealthVisitFlowReducers.select_someone_else({
          value: patientData?.full_name,
        })
      );
    }
    dispatch(
      behaviorHealthVisitFlowReducers.setPaymentMethod({
        value: incompleteData?.payment_method,
      })
    );
    dispatch(
      behaviorHealthVisitFlowReducers.select_condition({
        value: incompleteData?.skin_issue,
        isValid: incompleteData?.skin_issue?.length ? true : false,
        status:
          incompleteData?.behavior_health_steps?.sub_steps?.[
            bh_sub_steps.select_condition
          ] ?? "upcoming",
      })
    );
  }, [
    dispatch,
    incompleteData?.behavior_health_steps?.sub_steps,
    incompleteData?.skin_issue,
    incompleteData?.patient_id,
    incompleteData?.payment_method,
    patientsList,
  ]);

  const updateSlotDetails = useCallback(() => {
    dispatch(
      behaviorHealthVisitFlowReducers.select_slot({
        value: incompleteData?.meta?.booking_info,
        isValid:
          incompleteData?.meta?.booking_info &&
          Object.keys(incompleteData?.meta?.booking_info)?.length
            ? true
            : false,
        status:
          incompleteData?.behavior_health_steps?.sub_steps?.[
            bh_sub_steps.select_slot
          ] ?? "upcoming",
      })
    );
  }, [
    dispatch,
    incompleteData?.meta?.booking_info,
    incompleteData?.behavior_health_steps?.sub_steps,
  ]);

  const updateBasicDetails = useCallback(() => {
    dispatch(
      behaviorHealthVisitFlowReducers.confirm_residence({
        isValid: incompleteData?.state?.length ? true : false,
        status:
          incompleteData?.behavior_health_steps?.sub_steps?.[
            bh_sub_steps.confirm_residence
          ] ?? "upcoming",
      })
    );
    dispatch(
      behaviorHealthVisitFlowReducers.identity_confirmation({
        value: { uri: patientsList?.[0]?.insurance?.id_proof ?? "" },
        isValid: patientsList?.[0]?.insurance?.id_proof ? true : false,
        status:
          incompleteData?.behavior_health_steps?.sub_steps?.[
            bh_sub_steps.identity_confirmation
          ] ?? "upcoming",
      })
    );
  }, [
    dispatch,
    incompleteData?.behavior_health_steps?.sub_steps,
    incompleteData?.state?.length,
    patientsList,
  ]);

  const updateAdditionalDetails = useCallback(() => {
    dispatch(
      behaviorHealthVisitFlowReducers.select_pharmacy({
        value: incompleteData?.pharmacy_details,
        isValid: incompleteData?.pharmacy_details ? true : false,
        status:
          incompleteData?.behavior_health_steps?.sub_steps?.[
            bh_sub_steps.select_pharmacy
          ] ?? "upcoming",
      })
    );
    dispatch(
      behaviorHealthVisitFlowReducers.symptoms({
        value: incompleteData?.behavior_health_symptoms,
        isValid: incompleteData?.behavior_health_symptoms?.length
          ? true
          : false,
        status:
          incompleteData?.behavior_health_steps?.sub_steps?.[
            bh_sub_steps.symptoms
          ] ?? "upcoming",
      })
    );
    dispatch(
      behaviorHealthVisitFlowReducers.additional_info({
        value: {
          additional_info: incompleteData?.additional_info,
          additional_info_recording: incompleteData?.additional_info_recording,
        },
        isValid: true,
        status:
          incompleteData?.behavior_health_steps?.sub_steps?.[
            bh_sub_steps.additional_info
          ] ?? "upcoming",
      })
    );
  }, [
    dispatch,
    incompleteData?.pharmacy_details,
    incompleteData?.additional_info,
    incompleteData?.additional_info_recording,
    incompleteData?.behavior_health_steps?.sub_steps,
    incompleteData?.behavior_health_symptoms,
  ]);

  useEffect(() => {
    if (incompleteData && Object.keys(incompleteData)?.length && patientsList) {
      updateMainSteps();
      updateVisitDetails();
      updateSlotDetails();
      updateBasicDetails();
      updateAdditionalDetails();
    }
  }, [
    incompleteData,
    patientsList,
    updateMainSteps,
    updateBasicDetails,
    updateVisitDetails,
    updateSlotDetails,
    updateAdditionalDetails,
  ]);
}

export default usePopulateBehaviorHealthData;
